import { useContext, useState } from 'react';

import { Formik } from 'formik';
import { useNavigate } from 'react-router';
import * as Yup from 'yup';

import { Box, Button, Card, CircularProgress, Grid, styled, Typography } from '@mui/material';

import { SubmitButton } from '../../../components/buttons';
import { FormContainer } from '../../../components/forms';
import { TextField } from '../../../components/inputs';
import { AlertSnackbarContext } from '../../../components/snackbars';
import { Spinner } from '../../../components/spinners';
import { ErrorMessages } from '../../../constants';
import { AnnouncementCreateDTO, useCreateAnnouncement } from '../../../lib/announcement/hooks';
import useMediaUpload from '../../../lib/media/hooks/useMediaUpload';
import { Media } from '../../../types/Media';

type AnnouncementFormType = {
  id: string;
  title: string;
};

const AnnouncementCreateSchema = Yup.object().shape({
  title: Yup.string().required(ErrorMessages.REQUIRED_ANNOUNCEMENT_TITLE),
});

const AddNewAnnouncement = () => {
  const navigate = useNavigate();
  const [photoMedia, setPhotoMedia] = useState<Media | null>(null);
  const [, setAlertSnackbar] = useContext(AlertSnackbarContext);
  const { mutateAsync: createAnnouncement, isLoading: isSubmitting } = useCreateAnnouncement();

  const { upload, isUploading } = useMediaUpload({
    onSuccess: (data) => {
      setPhotoMedia(data);
    },
    onError: () => {
      setAlertSnackbar({
        message: ErrorMessages.SERVER_ERROR,
        severity: 'error',
      });
    },
  });

  const handleSubmit = async (values: AnnouncementFormType) => {
    if (!photoMedia) {
      setAlertSnackbar({
        message: ErrorMessages.REQUIRED_MEDIA_IMAGE,
        severity: 'error',
      });
      return;
    }

    let newAnnouncement: AnnouncementCreateDTO = {
      title: values.title,
      media: photoMedia as Media,
    };

    await createAnnouncement(newAnnouncement);
  };

  let ready = false;
  let initialValues: AnnouncementFormType = { id: '', title: '' };

  ready = true;

  const setInitValue = (value?: string | boolean) => value || ' ';

  return (
    <Container>
      <TopBar>
        <Typography variant="h5">Announcement</Typography>
      </TopBar>

      <StyledCard>
        {!ready && <Spinner />}
        {ready && initialValues && (
          <Formik initialValues={initialValues} validationSchema={AnnouncementCreateSchema} validateOnBlur={true} onSubmit={handleSubmit}>
            {({ values, errors, touched, handleChange, handleBlur }) => (
              <FormContainer>
                <Grid container rowSpacing={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={8}>
                      <TextField
                        error={touched.title && !!errors.title}
                        type="text"
                        name="title"
                        label="Title"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.title}
                        helperText={setInitValue(touched.title && errors.title)}
                        disabled={isSubmitting}
                      />
                    </Grid>

                    {/* Upload media */}
                    <Grid item xs={8}>
                      {!photoMedia ? (
                        <Button variant="contained" component="label" disabled={isSubmitting || isUploading}>
                          Upload Media
                          <input
                            type="file"
                            accept="image/png,image/jpeg"
                            hidden
                            disabled={isSubmitting || isUploading}
                            onChange={async (event) => {
                              const file = event.currentTarget.files && event.currentTarget.files[0];
                              if (file) {
                                if (!['image/png', 'image/jpeg'].includes(file.type)) {
                                  setAlertSnackbar({
                                    message: 'Only JPG or PNG files are allowed.',
                                    severity: 'error',
                                  });
                                  return;
                                }
                                await upload(file);
                              }
                            }}
                          />
                        </Button>
                      ) : (
                        <Button variant="outlined" onClick={() => setPhotoMedia(null)} disabled={isSubmitting || isUploading}>
                          Remove Media
                        </Button>
                      )}
                      {isUploading ? (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={16} />
                          <Typography variant="body2">Uploading photo...</Typography>
                        </Box>
                      ) : null}
                      {photoMedia ? (
                        <Box sx={{ mt: 2 }}>
                          <Typography>Uploaded Media</Typography>
                          <Box
                            component="img"
                            src={photoMedia?.smallUrl}
                            alt="Uploaded Media"
                            sx={{
                              objectFit: 'fill',
                              width: 'auto',
                            }}
                            width={300}
                            height={200}
                          />
                        </Box>
                      ) : null}
                    </Grid>
                  </Grid>
                </Grid>
                <ButtonContainer>
                  <Buttons>
                    <SubmitButton type="submit" disabled={isSubmitting || isUploading} loading={isSubmitting} variant="contained">
                      Save
                    </SubmitButton>
                    <Button onClick={() => navigate(-1)} type="button" disabled={isSubmitting || isUploading}>
                      Cancel
                    </Button>
                  </Buttons>
                </ButtonContainer>
              </FormContainer>
            )}
          </Formik>
        )}
      </StyledCard>
    </Container>
  );
};

const Container = styled('div')({
  padding: '20px',
  backgroundColor: '#eee',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
});

const TopBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
});

const StyledCard = styled(Card)({
  display: 'flex',
});

const ButtonContainer = styled('div')({
  marginTop: '20px',
  alignSelf: 'stretch',
  flex: 1,
  display: 'flex',
  justifyContent: 'space-between',
});

const Buttons = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',

  '& > *': {
    margin: '5px',
  },
});

export default AddNewAnnouncement;
